import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Stack } from "@mui/system";
import { Skeleton } from "@mui/material";
import DecreaseDailyIncomeFilter from "./DecreaseDailyIncomeFilter";
import DecreaseDailyIncomeItem from "./DecreaseDailyIncomeItem";
import {
  DECREASE_DAILY_INCOME_SORT_OPTIONS,
  DEFAULT_DECREASE_DAILY_INCOME_SORT,
  sortAnomaliesRows,
} from "../../Common/anomaliesSort";
import { exportRowsToXlsx } from "../../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/Utils/anomaliesColumns";
import { DECREASE_DAILY_INCOME_COLUMNS } from "../../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/AnomaliesIncome/DecreaseDailyIncomeTable";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";

const DecreaseDailyIncomeSkeleton = () => (
  <Box sx={{ mt: 2 }}>
    <Stack spacing={2}>
      {[1, 2, 3].map((item) => (
        <Box
          key={item}
          sx={{ borderRadius: 2, border: "1px solid #e0e0e0", p: 2 }}
        >
          <Stack spacing={1.2}>
            <Skeleton width={140} height={20} />
            <Skeleton width="60%" height={18} />
          </Stack>
        </Box>
      ))}
    </Stack>
  </Box>
);

const DecreaseDailyIncome = () => {
  const [searchParams] = useSearchParams();
  const workspaceParam = searchParams.get("workspace");
  const { fetchDecreaseDailyIncomeData, getSiteFromWorkspace } =
    useBizDevelopmentDashboardMobile();

  const isLoading = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderIncomeData,
  );
  const output = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.decreaseDailyIncomeData,
  );

  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;
    fetchDecreaseDailyIncomeData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  const [brandName, setBrandName] = useState("");
  const [sortKey, setSortKey] = useState(
    DEFAULT_DECREASE_DAILY_INCOME_SORT.key,
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    DEFAULT_DECREASE_DAILY_INCOME_SORT.dir,
  );

  const brandList = useMemo(() => {
    const set = new Set<string>();
    (output ?? []).forEach((row: Record<string, any>) => {
      const name = String(row?.brand_name ?? "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [output]);

  const filteredOutput = useMemo(() => {
    const search = brandName.trim().toLowerCase();
    if (!search) return output ?? [];
    return (output ?? []).filter((row: Record<string, any>) =>
      String(row?.brand_name ?? "")
        .toLowerCase()
        .includes(search),
    );
  }, [output, brandName]);

  const sortedOutput = useMemo(
    () => sortAnomaliesRows(filteredOutput, sortKey, sortDir),
    [filteredOutput, sortKey, sortDir],
  );

  const brandCount = filteredOutput.length;

  const handleApplySort = (key: string, dir: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(dir);
  };

  const handleDownload = () => {
    exportRowsToXlsx(
      sortedOutput,
      DECREASE_DAILY_INCOME_COLUMNS,
      "decrease-daily-income-mtd.xlsx",
    );
  };

  return (
    <>
      <DecreaseDailyIncomeFilter
        brandCount={brandCount}
        onDownload={handleDownload}
        downloadDisabled={isLoading || brandCount === 0}
        sortKey={sortKey}
        sortDir={sortDir}
        onApplySort={handleApplySort}
        sortOptions={DECREASE_DAILY_INCOME_SORT_OPTIONS}
        brandList={brandList}
        brandName={brandName}
        onBrandNameChange={setBrandName}
      />
      {isLoading ? (
        <DecreaseDailyIncomeSkeleton />
      ) : (
        <DecreaseDailyIncomeItem sortedData={sortedOutput} />
      )}
    </>
  );
};

export default DecreaseDailyIncome;
